import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Sparkles, TrendingUp, ArrowUpRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Phase 310.5 — AI Recovery ROI Hero Tile
 *
 * The hero metric on the owner dashboard. Reads from `/api/finance/roi-metrics`
 * and prints the ₹ recovered this calendar month alongside the multiplier
 * vs the academy's subscription price. Self-hides on fetch error (we never
 * want a broken tile to push the rest of the dashboard offscreen).
 */
function formatInr(value) {
  if (value === null || value === undefined) return '0';
  const n = Math.round(Number(value) || 0);
  // Indian thousand-separator system (lakhs/crores).
  const s = String(Math.abs(n));
  if (s.length <= 3) return (n < 0 ? '-' : '') + s;
  const head = s.slice(0, -3);
  const tail = s.slice(-3);
  const formatted = head.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + tail;
  return (n < 0 ? '-' : '') + formatted;
}

export default function RoiHeroTile() {
  const [roi, setRoi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/finance/roi-metrics')
      .then((res) => { if (alive) setRoi(res.data); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <Skeleton className="h-32 rounded-2xl" data-testid="roi-hero-loading" />;
  }
  if (error || !roi) return null;

  const recovered = Number(roi.recovered_this_month_amount || 0);
  const subscription = Number(roi.subscription_price_inr || 0);
  const multiplier = Number(roi.roi_multiplier || 0);
  const monthLabel = roi.month_label || '';
  const count = roi.recovered_this_month_count || 0;

  // Hide entirely on the FIRST month an academy enables the feature
  // (recovered = 0). Showing "₹0 recovered" looks underwhelming and
  // un-sells the feature. The tile reappears once the first defaulter pays.
  if (recovered <= 0) return null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 p-6 md:p-7 text-white shadow-lg ring-1 ring-emerald-700/50"
      data-testid="roi-hero-tile"
    >
      {/* Decorative blur orbs */}
      <div className="pointer-events-none absolute -top-12 -right-12 h-48 w-48 rounded-full bg-emerald-400/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-8 h-40 w-40 rounded-full bg-teal-400/10 blur-3xl" />

      <div className="relative flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-400/20 ring-1 ring-emerald-300/30 backdrop-blur">
            <Sparkles className="h-6 w-6 text-emerald-200" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-200/80">
              <TrendingUp className="h-3 w-3" /> AI Recovery · {monthLabel}
            </div>
            <h2
              className="mt-1.5 font-display text-3xl md:text-4xl font-semibold leading-tight"
              data-testid="roi-hero-amount"
            >
              AI Recovered{' '}
              <span className="tabular-nums text-emerald-200">
                ₹{formatInr(recovered)}
              </span>{' '}
              this month.
            </h2>
            <p className="mt-2 text-sm text-emerald-100/85 max-w-2xl" data-testid="roi-hero-subtext">
              {multiplier > 0 ? (
                <>
                  That&apos;s <strong className="text-white tabular-nums">{multiplier.toFixed(1)}×</strong> your Out-Play subscription
                  (₹{formatInr(subscription)}/month) — automated via Smart Win-backs.
                </>
              ) : (
                <>Automated via Smart Win-backs.</>
              )}
            </p>
            <p className="mt-1 text-xs text-emerald-200/70">
              {count} invoice{count === 1 ? '' : 's'} recovered through AI-drafted reminders.
            </p>
          </div>
        </div>
        <Link
          to="/finance"
          className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white ring-1 ring-white/20 backdrop-blur transition-colors hover:bg-white/20"
          data-testid="roi-hero-cta"
        >
          View ledger <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}
